import { LocalizedDatePipe } from '../../../shared/pipe/localized-date.pipe';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule, NonNullableFormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router, RouterModule } from '@angular/router';
import { distinctUntilChanged } from 'rxjs';

import { DataStateComponent } from '../../../shared/components/ui/data-state/data-state.component';
import { PageHeaderComponent } from '../../../shared/components/ui/page-header/page-header.component';
import { PAGE_SIZE_OPTIONS } from '../../../shared/components/ui/pretty-select/common-options';
import {
  PrettySelectComponent,
  PrettySelectOption,
} from '../../../shared/components/ui/pretty-select/pretty-select.component';
import { toErrorMessage } from '../../../shared/helpers/http-error';
import { WalletsService } from '../../../shared/services/wallets.service';
import {
  AccountInDebtView,
  OWNER_TYPE_LABEL,
  OwnerType,
} from '../../../shared/types/wallet.types';

const OWNER_TYPE_OPTIONS: PrettySelectOption<OwnerType>[] = [
  { value: 'OWNER', label: 'Bailleurs' },
  { value: 'OPERATOR', label: 'Opérateurs' },
  { value: 'DRIVER', label: 'Chauffeurs' },
];

const LOOKUP_OWNER_TYPE_OPTIONS: PrettySelectOption<OwnerType>[] = [
  { value: 'OWNER', label: 'Bailleur' },
  { value: 'OPERATOR', label: 'Opérateur' },
  { value: 'DRIVER', label: 'Chauffeur' },
  { value: 'PLATFORM', label: 'Plateforme' },
];

@Component({
  selector: 'app-wallets-list',
  imports: [LocalizedDatePipe, 
    CommonModule,
    ReactiveFormsModule,
    FormsModule,
    RouterModule,
    PageHeaderComponent,
    DataStateComponent,
    PrettySelectComponent,
  ],
  templateUrl: './wallets-list.component.html',
})
export class WalletsListComponent implements OnInit {
  private readonly service = inject(WalletsService);
  private readonly fb = inject(NonNullableFormBuilder);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  // ── Debt list ───────────────────────────────────────────────────────
  readonly loading = signal(false);
  readonly error = signal<string | null>(null);
  readonly items = signal<AccountInDebtView[]>([]);
  readonly totalItems = signal(0);
  readonly totalPages = signal(0);
  readonly page = signal(1);
  readonly size = signal(20);
  readonly ownerTypeCtrl = this.fb.control<OwnerType>('OWNER', [Validators.required]);

  // ── Lookup card (jump to a wallet by ownerType+ownerRef) ────────────
  readonly lookupForm = this.fb.group({
    ownerType: ['OWNER' as OwnerType, [Validators.required]],
    ownerRef: [null as number | null, [Validators.required, Validators.min(1)]],
  });

  readonly ownerTypeOptions = OWNER_TYPE_OPTIONS;
  readonly lookupOwnerTypeOptions = LOOKUP_OWNER_TYPE_OPTIONS;
  readonly pageSizeOptions = PAGE_SIZE_OPTIONS;
  readonly typeLabel = OWNER_TYPE_LABEL;

  readonly pageWindow = computed<number[]>(() => {
    const total = this.totalPages();
    if (total <= 1) return total === 1 ? [1] : [];
    const current = this.page();
    const window = 5;
    let start = Math.max(1, current - Math.floor(window / 2));
    const end = Math.min(total, start + window - 1);
    start = Math.max(1, end - window + 1);
    return Array.from({ length: end - start + 1 }, (_, i) => start + i);
  });

  ngOnInit(): void {
    this.ownerTypeCtrl.valueChanges
      .pipe(distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe(() => {
        this.page.set(1);
        this.load();
      });
    this.load();
  }

  load(): void {
    const ot = this.ownerTypeCtrl.value;
    if (!ot) return;
    this.loading.set(true);
    this.error.set(null);
    this.service
      .prestatairesInDebt(ot, this.page(), this.size())
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res) => {
          this.items.set(res.data ?? []);
          this.totalItems.set(res.totalItems ?? 0);
          this.totalPages.set(res.totalPages ?? 0);
          this.loading.set(false);
        },
        error: (err: unknown) => {
          this.loading.set(false);
          this.items.set([]);
          this.totalItems.set(0);
          this.totalPages.set(0);
          this.error.set(toErrorMessage(err));
        },
      });
  }

  goToPage(p: number): void {
    if (p < 1 || p > this.totalPages() || p === this.page()) return;
    this.page.set(p);
    this.load();
  }

  changeSize(s: number): void {
    if (!s || s === this.size()) return;
    this.size.set(s);
    this.page.set(1);
    this.load();
  }

  /** Jump directly to a wallet detail page from the lookup form. */
  submitLookup(): void {
    if (this.lookupForm.invalid) {
      this.lookupForm.markAllAsTouched();
      return;
    }
    const { ownerType, ownerRef } = this.lookupForm.getRawValue();
    void this.router.navigate(['/wallets', ownerType, ownerRef]);
  }
}
